import { FC } from 'react';

import { ErdModel, ErdRelationship } from '../types';

interface ErdDetailPanelProps {
  model: ErdModel;
  relationships: ErdRelationship[];
  onClose: () => void;
  onSetFocus: (modelId: string) => void;
}

export const ErdDetailPanel: FC<ErdDetailPanelProps> = ({
  model,
  relationships,
  onClose,
  onSetFocus,
}) => {
  const modelRelationships = relationships.filter(
    (r) => r.sourceModelId === model.uniqueId || r.targetModelId === model.uniqueId,
  );

  return (
    <div
      className="flex h-full w-80 flex-col border-l border-borderMuted bg-bgMain"
      data-testid="erd-detail-panel"
    >
      <div className="flex items-center justify-between border-b border-borderMuted p-4">
        <div>
          <h3 className="text-sm font-semibold text-fgMain">{model.name}</h3>
          <span className="text-xs text-fgDecorative">
            {model.database ? `${model.database}.` : ''}
            {model.schema}
          </span>
        </div>
        <button
          className="text-fgDecorative hover:text-fgMain"
          onClick={onClose}
          data-testid="erd-detail-close"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M12 4L4 12M4 4L12 12"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {model.description && (
          <div className="mb-4">
            <div className="mb-1 text-xs font-semibold text-fgDecorative">
              Description
            </div>
            <p className="text-sm text-fgMain">{model.description}</p>
          </div>
        )}

        {model.owner && (
          <div className="mb-4">
            <div className="mb-1 text-xs font-semibold text-fgDecorative">Owner</div>
            <p className="text-sm text-fgMain">{model.owner}</p>
          </div>
        )}

        {model.tags.length > 0 && (
          <div className="mb-4">
            <div className="mb-1 text-xs font-semibold text-fgDecorative">Tags</div>
            <div className="flex flex-wrap gap-1">
              {model.tags.map((tag) => (
                <span
                  key={tag}
                  className="rounded bg-bgNeutralMuted px-1.5 py-0.5 text-xs text-fgDecorative"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {model.columns.length > 0 && (
          <div className="mb-4">
            <div className="mb-1 text-xs font-semibold text-fgDecorative">
              Columns ({model.columns.length})
            </div>
            <div className="max-h-48 overflow-y-auto rounded border border-borderMuted">
              {model.columns.map((col) => (
                <div
                  key={col.name}
                  className="flex items-center gap-1.5 border-b border-borderMuted px-2 py-1 last:border-b-0"
                >
                  {col.isPrimaryKey && (
                    <span className="shrink-0 rounded bg-bgWarningMuted px-1 text-[10px] font-bold text-fgWarning">
                      PK
                    </span>
                  )}
                  {col.isForeignKey && (
                    <span className="shrink-0 rounded bg-bgBrandMuted px-1 text-[10px] font-bold text-fgBrand">
                      FK
                    </span>
                  )}
                  <span className="flex-1 truncate text-xs text-fgMain">
                    {col.name}
                  </span>
                  <span className="shrink-0 text-[10px] text-fgDecorative">
                    {col.type}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {modelRelationships.length > 0 && (
          <div className="mb-4">
            <div className="mb-1 text-xs font-semibold text-fgDecorative">
              Relationships ({modelRelationships.length})
            </div>
            {modelRelationships.map((rel) => {
              const isSource = rel.sourceModelId === model.uniqueId;
              const otherModelId = isSource ? rel.targetModelId : rel.sourceModelId;
              const otherModelName = otherModelId.split('.').pop() ?? otherModelId;
              const colorClass =
                rel.edgeSource === 'relationshipTest'
                  ? 'border-l-borderBrand'
                  : rel.edgeSource === 'semanticLayer'
                    ? 'border-l-borderSuccessMuted'
                    : 'border-l-borderWarning';

              return (
                <div
                  key={rel.id}
                  className={`mb-1 rounded border border-l-4 border-borderMuted ${colorClass} p-2`}
                >
                  <div className="text-xs font-medium text-fgMain">
                    {isSource ? `→ ${otherModelName}` : `← ${otherModelName}`}
                  </div>
                  <div className="text-[10px] text-fgDecorative">
                    {rel.sourceColumn} → {rel.targetColumn}
                  </div>
                  <div className="text-[10px] capitalize text-fgDecorative">
                    {rel.edgeSource === 'relationshipTest'
                      ? 'Relationship Test'
                      : rel.edgeSource === 'semanticLayer'
                        ? 'Semantic Layer'
                        : `Inferred (${rel.confidence ?? 'medium'})`}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="border-t border-borderMuted p-3">
        <button
          className="w-full rounded-md bg-bgBrandMuted px-3 py-1.5 text-sm font-medium text-fgBrand transition-colors hover:opacity-80"
          onClick={() => onSetFocus(model.uniqueId)}
          data-testid="erd-set-focus"
        >
          View relationships
        </button>
      </div>
    </div>
  );
};
